import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { dataService } from '../services/dataService';
import { Client } from '../types';
import { Plus, Users, Settings } from 'lucide-react';

export const MainDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const data = await dataService.getClients();
        setClients(data);
      } catch (error) {
        showToast('Failed to load clients', 'error');
      } finally {
        setIsLoading(false);
      }
    };

    fetchClients();
  }, [showToast]);

  const handleNavigateToClient = (clientId: string) => {
    navigate(`/clients/${clientId}`);
  };

  const handleUpdateClient = async (clientId: string, updates: Partial<Client>) => {
    try {
      const updatedClient = await dataService.updateClient(clientId, updates);
      setClients(prev => prev.map(client => 
        client.id === clientId ? updatedClient : client
      ));
      showToast('Client updated successfully', 'success');
    } catch (error) {
      showToast('Failed to update client', 'error');
    }
  };

  const handleAddClient = async (newClient: Client) => {
    try {
      const createdClient = await dataService.createClient(newClient);
      setClients(prev => [...prev, createdClient]);
      showToast('Client created successfully', 'success');
    } catch (error) {
      showToast('Failed to create client', 'error');
    }
  };

  const handleDeleteClient = async (clientId: string) => {
    try {
      await dataService.deleteClient(clientId);
      setClients(prev => prev.filter(client => client.id !== clientId));
      showToast('Client deleted successfully', 'success');
    } catch (error) {
      showToast('Failed to delete client', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
        <div className="flex space-x-4">
          {user?.role === 'admin' && (
            <button
              onClick={() => navigate('/users')}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <Users size={16} />
              <span>Users</span>
            </button>
          )}
          <button
            onClick={() => navigate('/profile')}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Settings size={16} />
            <span>Settings</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {clients.map(client => (
          <div
            key={client.id}
            className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => handleNavigateToClient(client.id)}
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{client.name}</h2>
            <p className="text-gray-600 mb-4">{client.description}</p>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-500">
                {client.bannerSets?.length || 0} Banner Sets
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClient(client.id);
                }}
                className="text-red-600 hover:text-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        ))}

        <button
          onClick={() => handleAddClient({
            id: '',
            name: 'New Client',
            description: 'Add a description',
            bannerSets: [],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          })}
          className="flex items-center justify-center space-x-2 bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow border-2 border-dashed border-gray-300"
        >
          <Plus size={24} className="text-gray-400" />
          <span className="text-gray-600">Add New Client</span>
        </button>
      </div>
    </div>
  );
};